import type { HeartRateEvent } from '@model/DeviceConfiguration';
import { applyAiRepairs } from './applyAiRepairs';
import { buildRepairSummaryPrompt } from './buildRepairSummaryPrompt';
import { callOpenAIRepair } from './client';
import { detectHrDataIssues, hasRepairableIssues } from './detectHrDataIssues';
import {
  buildDeterministicRepairOperations,
  buildRepairSummary,
} from './normalizeRepairOperations';
import { parseValidationResponse } from './parseValidationResponse';
import type { AiRepairSessionResult, AiRepairValidation } from './types';
import { validateRepairDeterministic } from './validateRepairDeterministic';

const buildLocalAiValidation = (
  summary: ReturnType<typeof buildRepairSummary>
): AiRepairValidation => ({
  status: 'verified',
  summary: `Riparazione completata: ${summary.duplicateMergeOperations} duplicati uniti, ${summary.gapInsertionOperations} secondi interpolati (${summary.correctionsCount} correzioni totali).`,
  findings: [],
});

export const repairHeartRateEventsWithAi = async (
  originalEvents: HeartRateEvent[]
): Promise<AiRepairSessionResult> => {
  const originalCount = originalEvents.length;
  const issues = detectHrDataIssues(originalEvents);

  if (!hasRepairableIssues(issues)) {
    return {
      status: 'no-op',
      message:
        'Non sono stati trovati gap o duplicati riparabili nei dati originali.',
      originalCount,
    };
  }

  try {
    const operations = buildDeterministicRepairOperations(
      originalEvents,
      issues
    );
    const applied = applyAiRepairs(originalEvents, operations);
    const deterministic = validateRepairDeterministic(
      originalEvents,
      applied.repairedEvents,
      operations
    );

    if (!deterministic.passed) {
      return {
        status: 'failed',
        message: 'Validazione deterministica fallita: riparazione non salvata.',
        originalCount,
        validation: { deterministic },
      };
    }

    const summary = buildRepairSummary(
      originalEvents,
      issues,
      operations,
      applied
    );

    let aiValidation: AiRepairValidation;

    try {
      const validationMessages = buildRepairSummaryPrompt(
        issues,
        operations,
        deterministic,
        summary
      );
      const { content: validationContent } =
        await callOpenAIRepair(validationMessages);
      aiValidation = parseValidationResponse(validationContent);

      if (
        aiValidation.status !== 'verified' &&
        deterministic.passed &&
        summary.countsMatchExpected
      ) {
        aiValidation = {
          status: 'verified',
          summary:
            aiValidation.summary ||
            'Riparazione accettata: controlli locali superati.',
          findings: [
            ...aiValidation.findings,
            'Nota validatore AI ignorata: controlli hard già superati.',
          ],
        };
      }
    } catch {
      aiValidation = buildLocalAiValidation(summary);
    }

    if (aiValidation.status !== 'verified') {
      return {
        status: 'failed',
        message:
          aiValidation.summary ||
          'Validazione OpenAI ha rifiutato la riparazione.',
        originalCount,
        validation: {
          deterministic,
          ai: aiValidation,
        },
      };
    }

    return {
      status: 'success',
      message: aiValidation.summary,
      repairedEvents: applied.repairedEvents,
      repairedOnly: applied.repairedOnly,
      originalCount,
      repairedCount: applied.repairedEvents.length,
      duplicateMerges: applied.duplicateMerges,
      gapFillCount: applied.gapFillCount,
      validation: {
        deterministic,
        ai: aiValidation,
      },
    };
  } catch (error) {
    return {
      status: 'failed',
      message:
        error instanceof Error ? error.message : 'Riparazione AI fallita.',
      originalCount,
    };
  }
};
